import * as Sentry from '@sentry/browser'
import { toRaw } from 'vue'
import { addSessionBreadcrumb } from './sentrySessionBreadcrumb'
import { getLastReloadDebugPayload } from '../../pinia/reloadDebug'

const networkErrorLastCaptured = new Map()
const NETWORK_CAPTURE_THROTTLE_MS = 60_000

/** Offline, aborted, or slow connection (incl. axios 30s timeout). */
export const isAxiosNetworkError = (error) => {
    if (!error) return false
    const code = error.code
    const message = String(error.message || '').toLowerCase()
    const status = error.response?.status
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true
    if (message.includes('network error')) return true
    if (message.includes('timeout') && message.includes('exceeded')) return true
    return status === 0
}

export const axiosErrorDiagnostics = (error) => {
    const config = error?.config || {}
    const url = config.url || error?.request?.responseURL || ''
    return {
        url: String(url),
        method: String(config.method || 'get').toUpperCase(),
        status: error?.response?.status ?? null,
        code: error?.code ?? null,
        message: String(error?.message || ''),
        timeoutMs: config.timeout ?? null,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
    }
}

/** Client-side aborts / background tabs — not actionable API failures. */
export const isExpectedClientNetworkFailure = (error, diag = null) => {
    const d = diag || axiosErrorDiagnostics(error)
    if (d.visibility === 'hidden') return true
    if (d.online === false) return true
    const code = error?.code ?? d.code
    if (code === 'ERR_CANCELED' || code === 'CanceledError') return true
    return false
}

/** Backend JSON body from failed axios responses (e.g. TestResultsController codes 1000–1005). */
export const axiosResponsePayload = (error) => {
    const data = error?.response?.data
    if (!data || typeof data !== 'object') return {}
    return {
        backendCode: data.code ?? null,
        backendMessage: data.message ?? null,
        backendError: data.error ?? null,
        questionId: data.questionId ?? null,
    }
}

const httpErrorLastCaptured = new Map()
const HTTP_CAPTURE_THROTTLE_MS = 60_000

/** Breadcrumb + throttled Sentry for HTTP 4xx/5xx (not offline/network). Does not throw. */
export const reportAxiosHttpError = (error, where, extra = {}) => {
    const { sentryArea = 'etrainer-api', ...restExtra } = extra

    if (isAxiosNetworkError(error)) {
        reportAxiosNetworkError(error, where, restExtra)
        return
    }

    const diag = axiosErrorDiagnostics(error)
    const payload = axiosResponsePayload(error)
    console.warn(where, diag.message || 'HTTP error', diag, payload, restExtra)

    try {
        Sentry.addBreadcrumb({
            category: 'xhr',
            level: 'error',
            message: `[http] ${where}`,
            data: { where, ...diag, ...payload, ...restExtra },
        })
    } catch (_) { /* ignore */ }

    const fingerprint = `${where}:${diag.method}:${diag.url}:${payload.backendCode || diag.status}`
    const now = Date.now()
    const last = httpErrorLastCaptured.get(fingerprint) || 0
    if (now - last < HTTP_CAPTURE_THROTTLE_MS) return
    httpErrorLastCaptured.set(fingerprint, now)

    const status = diag.status
    const level = status >= 500 ? 'error' : 'warning'
    const summary = payload.backendCode
        ? `API ${payload.backendCode}: ${payload.backendError || payload.backendMessage || diag.message}`
        : `HTTP ${status}: ${diag.method} ${diag.url}`

    Sentry.captureMessage(summary, {
        level,
        tags: {
            area: sentryArea,
            where,
            kind: 'http',
            httpStatus: status != null ? String(status) : 'unknown',
            backendCode: payload.backendCode != null ? String(payload.backendCode) : '',
        },
        extra: { where, ...diag, ...payload, ...restExtra },
        fingerprint: ['etrainer-http', where, diag.url, String(payload.backendCode || status)],
    })
}

/** Breadcrumb + throttled Sentry message; does not throw. */
export const reportAxiosNetworkError = (error, where, extra = {}) => {
    const diag = axiosErrorDiagnostics(error)
    if (isExpectedClientNetworkFailure(error, diag)) {
        return
    }

    console.warn(where, diag.message || 'Network error', diag, extra)

    try {
        Sentry.addBreadcrumb({
            category: 'xhr',
            level: 'warning',
            message: `[network] ${where}`,
            data: { where, ...diag, ...extra },
        })
    } catch (_) { /* ignore */ }

    const fingerprint = `${where}:${diag.method}:${diag.url}`
    const now = Date.now()
    const last = networkErrorLastCaptured.get(fingerprint) || 0
    if (now - last < NETWORK_CAPTURE_THROTTLE_MS) return
    networkErrorLastCaptured.set(fingerprint, now)

    Sentry.captureMessage(`Network request failed: ${diag.method} ${diag.url}`, {
        level: 'warning',
        tags: { area: 'etrainer-api', where, kind: 'network' },
        extra: { where, ...diag, ...extra },
        fingerprint: ['etrainer-network', where, diag.url],
    })
}

export const isAxiosHttpError = (error) => {
    if (!error) return false
    if (error.isAxiosError && error.response?.status) return true
    return Boolean(error.response?.status)
}

/** Error.message is non-enumerable — JSON.stringify(Error) is "{}" and breaks Sentry titles. */
export const extractRejectionMessage = (reason) => {
    if (reason == null) return String(reason)
    if (typeof reason === 'string') return reason
    if (reason instanceof Error) {
        return reason.message || reason.name || 'Error'
    }

    const candidates = [reason]
    try {
        const raw = toRaw(reason)
        if (raw !== reason) candidates.push(raw)
    } catch (_) {
        /* ignore */
    }

    for (const value of candidates) {
        if (value instanceof Error) {
            const text = value.message || value.name
            if (text) return String(text)
        }
        if (value && typeof value === 'object') {
            const msg = value.message
            if (msg != null && String(msg)) return String(msg)
            try {
                const json = JSON.stringify(value)
                if (json && json !== '{}') return json
            } catch (_) {
                /* ignore */
            }
        }
    }

    return String(reason)
}

const normalizeRejectionReason = (reason) => {
    if (reason instanceof Error) return reason
    if (typeof reason === 'string') return new Error(reason)
    return new Error(extractRejectionMessage(reason))
}

/**
 * Report unhandled promise rejections with app context. Call with event.preventDefault()
 * so Sentry's global handler skips duplicate capture (bootstrap registers before Sentry.init).
 */
export const reportUnhandledRejection = (reason, extra = {}) => {
    const original = reason
    let error = normalizeRejectionReason(reason)

    const rejectionMessage = extractRejectionMessage(original)
    const truncatedAppleLa =
        rejectionMessage === 'La' &&
        typeof navigator !== 'undefined' &&
        /iPhone|iPad|iPod/i.test(navigator.userAgent)

    if (truncatedAppleLa) {
        error = new Error(
            'Unhandled rejection on Apple WebKit (browser truncated message to "La"; investigate stack / call depth)'
        )
        error.cause = original instanceof Error ? original : undefined
    }

    const context = {
        routeHash: typeof window !== 'undefined' ? window.location.hash : undefined,
        lastReloadDebug: getLastReloadDebugPayload(),
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        rejectionType: original === null ? 'null' : typeof original,
        ...extra,
    }

    if (!(original instanceof Error)) {
        context.rejectionValue = original
    }

    console.error('[unhandledrejection]', error.message, context, original)

    Sentry.captureException(error, {
        level: 'error',
        tags: {
            area: 'etrainer-app',
            where: 'unhandledrejection',
            apple_truncated_la: truncatedAppleLa ? 'true' : 'false',
        },
        extra: context,
        fingerprint: truncatedAppleLa
            ? ['unhandled-rejection', 'apple-webkit-truncated-la']
            : ['unhandled-rejection', error.name, error.message.slice(0, 120)],
    })
}

/** Guest read-only chat SPA (`/chat/public`) or in-app `#/chat…` routes. */
export const isGuestChatHashRoute = () =>
    typeof window !== 'undefined' &&
    window.location.hash.toLowerCase().startsWith('#/chat')

export const isGuestChatPublicPage = () =>
    typeof window !== 'undefined' &&
    /\/chat\/public\/?$/i.test(window.location.pathname)

/** Skip dashboard `/usersettings` preload on the guest chat document (`/chat/public`). */
export const isGuestChatContext = () => isGuestChatPublicPage()

/** Laravel/Sanctum-style session 401 JSON — not an app-specific API failure. */
export const isStandardSessionUnauthorizedPayload = (payload) => {
    if (payload.backendCode != null) return false

    const text = String(payload.backendMessage || payload.backendError || '').toLowerCase()
    if (!text) return true
    return (
        text.includes('unauthenticated') ||
        text.includes('unauthorized') ||
        text.includes('nicht angemeldet') ||
        text.includes('not authenticated')
    )
}

/** Report only app error codes or non-standard 401 bodies; missing ebus is actionable. */
export const shouldReportSession401ToSentry = (payload, ebus) => {
    if (!ebus) return true
    if (payload.backendCode != null) return true
    return !isStandardSessionUnauthorizedPayload(payload)
}

/** @deprecated use shouldReportSession401ToSentry */
export const shouldReportDashboardUserSettings401 = shouldReportSession401ToSentry

/** Global unhandledrejection handler for axios HTTP errors (incl. session 401). */
export const handleUnhandledAxiosHttpRejection = (error) => {
    if (!isAxiosHttpError(error)) return

    const status = error.response?.status
    const payload = axiosResponsePayload(error)
    const ebus = typeof window !== 'undefined' ? window.$_ebus : null

    if (status === 401) {
        checkApiResponseForAuth(ebus, error)
        if (shouldReportSession401ToSentry(payload, ebus)) {
            reportAxiosHttpError(error, 'unhandledrejection.http', {
                authOutcome: ebus ? 'unexpected-401' : 'unexpected-401-no-ebus',
            })
        }
        return
    }

    reportAxiosHttpError(error, 'unhandledrejection.http')
}

/**
 * Dashboard route guard: session expiry → logout flow; plain 401 → no Sentry noise.
 * Non-401 and 401 with backend error payload still reported.
 */
export const handleDashboardUserSettingsError = (error) => {
    if (!error?.response) return

    const status = error.response.status
    const payload = axiosResponsePayload(error)
    const ebus = typeof window !== 'undefined' ? window.$_ebus : null
    const requestUrl = error.config?.url

    if (status === 401) {
        checkApiResponseForAuth(ebus, error)

        if (shouldReportSession401ToSentry(payload, ebus)) {
            reportAxiosHttpError(error, 'dashboard.user-settings', {
                sentryArea: 'etrainer-test',
                authOutcome: ebus ? 'unexpected-401' : 'unexpected-401-no-ebus',
                status,
                url: requestUrl,
            })
        }
        return
    }

    reportCaughtError(error, 'dashboard.user-settings', {
        status,
        url: requestUrl,
    })
}

export const reportCaughtError = (error, where, extra = {}) => {
    if (isAxiosHttpError(error)) {
        reportAxiosHttpError(error, where, { sentryArea: 'etrainer-test', ...extra })
        return
    }

    console.warn(where, error)
    const exception = error instanceof Error ? error : new Error(String(error || where))
    Sentry.captureException(exception, {
        tags: { area: 'etrainer-test', where },
        extra: { where, ...extra },
    })
}

// Re-entry guard: when a session expires, many parallel API calls fail with 401
// at once. Emit sessionEnded only once per page load to avoid retriggering the
// logout flow for every failed request (the flow always ends in a full page
// redirect, which resets this flag).
let sessionEndedEmitted = false

/** @internal Vitest only */
export const resetSessionEndedGuardForTests = () => {
    sessionEndedEmitted = false
}

export const checkApiResponseForAuth = (ebus,error) => {
    if (isAxiosNetworkError(error)) {
        reportAxiosNetworkError(error, 'api.auth-check')
        return
    }

    const status = error?.response?.status
    const url = error?.config?.url || error?.message || ''

    if (status === 401) {
        console.warn('Session expired:', url)
    } else {
        console.warn('API error:', status, url)
    }

    if (error && error.response && error.response.status === 401 && ebus) {
        if (sessionEndedEmitted) {
            addSessionBreadcrumb('401 ignored, sessionEnded already emitted', { url, status })
            return
        }

        sessionEndedEmitted = true
        addSessionBreadcrumb('401 emitting sessionEnded', { url, status })
        ebus.sessionEnded()
    }
}